import { useState, useEffect, useRef, useCallback } from "react";
import { useAppSelector } from "../app/hooks";
import { SERVER_URL } from "../config/config";
import {
  MAX_MESSAGE_HISTORY,
  MAX_MESSAGE_LENGTH,
  containsLinks as containsLinksInText,
  removeLinks as removeLinksFromText,
} from "../utils/chat";

export interface ChatMessage {
  id: number;
  username: string;
  message: string;
  timestamp: string;
  userId?: string;
  family?: string;
  position?: number; // Add position field for family hierarchy
}

export type ConnectionStatus = "connecting" | "connected" | "disconnected";

export interface UseWebSocketReturn {
  messages: ChatMessage[];
  connectionStatus: ConnectionStatus;
  sendMessage: (message: string) => boolean;
  clearMessages: () => void;
  reconnect: () => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const NORMAL_CLOSE_CODES = new Set([1000, 1001]);

const getWebSocketUrl = () =>
  SERVER_URL.replace("http://", "ws://").replace("https://", "wss://");

const formatTimestamp = (timestamp: number | string): string => {
  const date =
    typeof timestamp === "number"
      ? new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000)
      : new Date(timestamp);

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${month}/${day}: ${hours}:${minutes}`;
};

const truncateMessage = (message: string): string => {
  const cleanMessage = removeLinksFromText(message);

  if (cleanMessage.length <= MAX_MESSAGE_LENGTH) {
    return cleanMessage;
  }
  return cleanMessage.substring(0, MAX_MESSAGE_LENGTH - 3) + "...";
};

const limitMessageHistory = (messageArray: ChatMessage[]): ChatMessage[] => {
  if (messageArray.length <= MAX_MESSAGE_HISTORY) {
    return messageArray;
  }
  return messageArray.slice(0, MAX_MESSAGE_HISTORY);
};

const detachAndClose = (socket: WebSocket | null, code = 1000, reason = "Manual disconnect") => {
  if (!socket) {
    return;
  }

  socket.onopen = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onclose = null;

  if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
    socket.close(code, reason);
  }
};

const useWebSocket = (): UseWebSocketReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("disconnected");

  const { familyList } = useAppSelector((state) => state.family);
  const { signature, signMsg, account, isAuthorized } = useAppSelector(
    (state) => state.auth
  );
  const { myProfile } = useAppSelector((state) => state.profile);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isManuallyClosedRef = useRef(false);
  const connectRef = useRef<() => void>(() => {});

  const latestRef = useRef({
    familyList,
    signature,
    signMsg,
    account,
    isAuthorized,
    myProfileId: myProfile?.id,
  });
  latestRef.current = {
    familyList,
    signature,
    signMsg,
    account,
    isAuthorized,
    myProfileId: myProfile?.id,
  };

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      const { familyList: families } = latestRef.current;

      if (data.type === "history" && Array.isArray(data.data)) {
        let historyMessages: ChatMessage[] = data.data.map(
          (msg: any, index: number) => {
            const family = families.find(
              (item) => item.id === msg.user.family
            );

            return {
              id: msg.id || Date.now() + index,
              username:
                msg.user?.name ||
                msg.username ||
                `User${msg.user_id || msg.userId || "Unknown"}`,
              message: truncateMessage(msg.message || msg.text || ""),
              timestamp: formatTimestamp(msg.timestamp),
              userId:
                msg.user_id?.toString() ||
                msg.userId?.toString() ||
                msg.user?.id?.toString(),
              family: family?.name || "No Family",
              position: msg.user?.position,
            };
          }
        );

        historyMessages = limitMessageHistory(historyMessages);
        setMessages(historyMessages);
      } else if (data.type === "message") {
        const messageData = data.data;
        const family = families.find(
          (item) => item.id === messageData.user.family
        );

        const newMessage: ChatMessage = {
          id: messageData.id || Date.now(),
          username:
            messageData.user?.name ||
            messageData.username ||
            `User${messageData.userId || "Unknown"}`,
          message: truncateMessage(
            messageData.message || messageData.text || ""
          ),
          timestamp: formatTimestamp(messageData.timestamp || Date.now()),
          userId: messageData.user?.id?.toString(),
          family: family?.name || "No Family",
          position:
            messageData.user?.position || messageData.position || null,
        };

        setMessages((prev) => {
          const exists = prev.some((msg) => msg.id === newMessage.id);
          if (exists) return prev;

          return limitMessageHistory([newMessage, ...prev]);
        });
      }
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  }, []);

  const handleOpen = useCallback(() => {
    console.log("WebSocket connected");
    setConnectionStatus("connected");
    reconnectAttemptsRef.current = 0;

    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    const { signature: authSignature, signMsg: authSignMsg, account: authAccount, myProfileId } =
      latestRef.current;

    socket.send(
      JSON.stringify({
        signature: authSignature,
        signMsg: authSignMsg,
        body: {
          type: "history",
          userId: myProfileId || authAccount,
          limit: MAX_MESSAGE_HISTORY,
        },
      })
    );
  }, []);

  const handleClose = useCallback(
    (event: CloseEvent) => {
      console.log("WebSocket disconnected:", event.code, event.reason);
      setConnectionStatus("disconnected");
      wsRef.current = null;

      const { isAuthorized: authorized, signature: authSignature, signMsg: authSignMsg, account: authAccount } =
        latestRef.current;

      const shouldReconnect =
        !isManuallyClosedRef.current &&
        !NORMAL_CLOSE_CODES.has(event.code) &&
        reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS &&
        authorized &&
        authSignature &&
        authSignMsg &&
        authAccount;

      if (!shouldReconnect) {
        return;
      }

      const delay = Math.pow(2, reconnectAttemptsRef.current) * 1000;
      console.log(
        `Attempting to reconnect in ${delay}ms (attempt ${
          reconnectAttemptsRef.current + 1
        })`
      );

      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectAttemptsRef.current += 1;
        connectRef.current();
      }, delay);
    },
    []
  );

  const handleError = useCallback((error: Event) => {
    console.error("WebSocket error:", error);
    setConnectionStatus("disconnected");
  }, []);

  const connect = useCallback(() => {
    const { isAuthorized: authorized, signature: authSignature, signMsg: authSignMsg, account: authAccount } =
      latestRef.current;

    if (!authorized || !authSignature || !authSignMsg || !authAccount) {
      return;
    }

    const existing = wsRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    clearReconnectTimeout();
    isManuallyClosedRef.current = false;

    try {
      const wsUrl = getWebSocketUrl();
      console.log("Connecting to WebSocket:", wsUrl);

      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;
      setConnectionStatus("connecting");

      socket.onopen = handleOpen;
      socket.onmessage = handleMessage;
      socket.onclose = handleClose;
      socket.onerror = handleError;
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      setConnectionStatus("disconnected");
    }
  }, [clearReconnectTimeout, handleOpen, handleMessage, handleClose, handleError]);

  connectRef.current = connect;

  const closeWebSocket = useCallback(() => {
    isManuallyClosedRef.current = true;
    clearReconnectTimeout();
    detachAndClose(wsRef.current);
    wsRef.current = null;
    reconnectAttemptsRef.current = 0;
    setConnectionStatus("disconnected");
  }, [clearReconnectTimeout]);

  const sendMessage = useCallback(
    (messageText: string): boolean => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.error("WebSocket is not connected");
        return false;
      }

      const { signature: authSignature, signMsg: authSignMsg, account: authAccount, myProfileId } =
        latestRef.current;

      if (!authSignature || !authSignMsg || !authAccount) {
        console.error("Missing authentication data");
        return false;
      }

      if (containsLinksInText(messageText)) {
        console.warn("Links are not allowed in messages");
        return false;
      }

      if (messageText.length > MAX_MESSAGE_LENGTH) {
        console.warn(
          `Message too long. Max length is ${MAX_MESSAGE_LENGTH} characters.`
        );
        return false;
      }

      if (messageText.trim().length === 0) {
        console.warn("Cannot send empty message");
        return false;
      }

      try {
        wsRef.current.send(
          JSON.stringify({
            signature: authSignature,
            signMsg: authSignMsg,
            body: {
              userId: myProfileId || authAccount,
              message: messageText.trim(),
            },
          })
        );
        return true;
      } catch (error) {
        console.error("Failed to send message:", error);
        return false;
      }
    },
    []
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const reconnect = useCallback(() => {
    closeWebSocket();
    reconnectAttemptsRef.current = 0;
    isManuallyClosedRef.current = false;
    connect();
  }, [closeWebSocket, connect]);

  useEffect(() => {
    if (!isAuthorized || !signature || !signMsg || !account) {
      closeWebSocket();
      return;
    }

    connect();

    return () => {
      closeWebSocket();
    };
  }, [isAuthorized, signature, signMsg, account, connect, closeWebSocket]);

  return {
    messages,
    connectionStatus,
    sendMessage,
    clearMessages,
    reconnect,
  };
};

export default useWebSocket;
